/**
 * Карточки результата в ленте ассистента (US-10, §«Дизайн»):
 * разбивка-бары, таблица, график, шаги гида, пары «ключ — значение» и
 * аналитическая записка. Данные приходят с бэка — компонент только рисует.
 */
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../ui/chart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';
import type {
  AssistantBarsBlock,
  AssistantBlock,
  AssistantNoteBlock,
  AssistantSeriesBlock,
  AssistantTableBlock,
  NoteFormat,
} from '../../types/assistant';

/** brand-600 — серия графика в фирменном зелёном. */
const LINE_COLOR = '#2C9C42';
/** surface-border — сетка графика. */
const GRID_COLOR = '#E4E9E5';

const numberFormat = new Intl.NumberFormat('ru-RU');
const fmt = (value: number): string => numberFormat.format(value);

const BlockTitle = ({
  title,
  subtitle,
}: {
  title: string | null;
  subtitle: string | null;
}) => {
  if (title === null && subtitle === null) return null;
  return (
    <div className="mb-3">
      {title !== null && (
        <div className="text-sm font-bold leading-tight">{title}</div>
      )}
      {subtitle !== null && (
        <div className="mt-0.5 text-xs text-text-faint">{subtitle}</div>
      )}
    </div>
  );
};

const BarsBlock = ({ block }: { block: AssistantBarsBlock }) => {
  const max = Math.max(...block.rows.map((row) => Math.abs(row.value)), 0);
  return (
    <section data-testid="assistant-block-bars">
      <BlockTitle title={block.title} subtitle={block.subtitle} />
      <div className="space-y-2">
        {block.rows.map((row) => (
          <div key={row.label} className="flex items-center gap-2 text-xs">
            <span className="w-28 shrink-0 truncate text-text" title={row.label}>
              {row.label}
            </span>
            <span className="h-4 flex-1 overflow-hidden rounded bg-brand-50">
              <span
                className="block h-full rounded bg-brand-500"
                style={{
                  width: max > 0 ? `${(Math.abs(row.value) / max) * 100}%` : '0%',
                }}
              />
            </span>
            <span className="w-14 shrink-0 text-right font-bold tabular-nums text-brand-700">
              {fmt(row.value)}
              {block.unit ?? ''}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

const TableBlock = ({ block }: { block: AssistantTableBlock }) => (
  <section data-testid="assistant-block-table">
    <BlockTitle title={block.title} subtitle={block.subtitle} />
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {block.columns.map((column) => (
              <TableHead
                key={column.key}
                className={cn('text-xs', column.numeric && 'text-right')}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {block.rows.map((row, index) => (
            // Строки бэка не имеют идентификатора: ключ — порядок в ответе,
            // а он для одного ответа неизменен.
            <TableRow key={index}>
              {block.columns.map((column) => {
                const cell = row[column.key];
                return (
                  <TableCell
                    key={column.key}
                    className={cn(
                      'text-xs',
                      column.numeric && 'text-right font-bold tabular-nums',
                    )}
                  >
                    {typeof cell === 'number' ? fmt(cell) : (cell ?? '—')}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </section>
);

const SeriesBlock = ({ block }: { block: AssistantSeriesBlock }) => (
  <section data-testid="assistant-block-series">
    <BlockTitle title={block.title} subtitle={block.subtitle} />
    <ChartContainer
      config={{ value: { label: block.unit ?? '', color: LINE_COLOR } }}
      className="h-40 w-full"
    >
      <LineChart data={block.points} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
        <CartesianGrid vertical={false} stroke={GRID_COLOR} />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} width={44} />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Line
          dataKey="value"
          type="monotone"
          stroke={LINE_COLOR}
          strokeWidth={2.5}
          dot={{ r: 3, fill: LINE_COLOR }}
        />
      </LineChart>
    </ChartContainer>
  </section>
);



const NoteBlock = ({
  block,
  onExport,
  exporting,
}: {
  block: AssistantNoteBlock;
  onExport: (format: NoteFormat) => void;
  exporting: NoteFormat | null;
}) => {
  const { t } = useTranslation();
  return (
    <section data-testid="assistant-block-note" className="text-xs">
      <header className="mb-3 border-l-[3px] border-brand-500 pl-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-text-faint">
          {t('assistant.note.kind')}
        </div>
        <h3 className="mt-1 text-sm font-bold text-brand-700">{block.heading}</h3>
        {block.subtitle !== null && (
          <div className="mt-0.5 text-[10px] text-text-faint">
            {block.subtitle}
          </div>
        )}
        {/* Пометку об авторстве и дате печатает бэк (FR-5.5) — свою не
            сочиняем, иначе в документе и в выгруженном файле будут разные. */}
        <div className="mt-0.5 text-[10px] text-text-faint">{block.footer}</div>
      </header>
      {block.sections.map((section) => (
        <div key={section.heading} className="mb-3 last:mb-0">
          <h4 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-brand-700">
            {section.heading}
          </h4>
          {section.text !== null && (
            <p className="mb-1.5 whitespace-pre-line">{section.text}</p>
          )}
        </div>
      ))}
      {block.formats.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {block.formats.map((format) => (
            <Button
              key={format}
              type="button"
              variant="outline"
              size="sm"
              disabled={exporting !== null}
              onClick={() => onExport(format)}
              data-testid={`assistant-export-${format}`}
            >
              {t(`assistant.note.export.${format}`)}
            </Button>
          ))}
        </div>
      )}
    </section>
  );
};

interface ResultBlockProps {
  block: AssistantBlock;
  onExport: (format: NoteFormat) => void;
  exporting: NoteFormat | null;
}

/** Одна карточка результата во всю ширину ленты. */
export const ResultBlock = ({ block, onExport, exporting }: ResultBlockProps) => {
  const content = (() => {
    switch (block.kind) {
      case 'bars':
        return <BarsBlock block={block} />;
      case 'table':
        return <TableBlock block={block} />;
      case 'series':
        return <SeriesBlock block={block} />;
      case 'note':
        return (
          <NoteBlock block={block} onExport={onExport} exporting={exporting} />
        );
      default:
        return null;
    }
  })();
  if (content === null) return null;
  return (
    <div className="ml-10 animate-fade-up rounded-card border border-surface-border bg-surface p-3.5 shadow-card">
      {content}
    </div>
  );
};
